<template>
  <div class="lp">
    <crm-toolbar
      entity="lead"
      :filters="filters"
      :views="views"
      :view.sync="view"
      :statuses="statuses"
      @change="setFilters"
    >
      <template #actions>
        <v-btn v-if="canManage" outlined small color="grey darken-1" class="lp__btn" @click="shell.manageStatuses('lead')">
          <v-icon small class="me-1">mdi-cog-outline</v-icon>{{ $t('crmStatusSettings') }}
        </v-btn>
        <v-btn v-if="canCreate" outlined small color="primary" class="lp__btn" @click="shell.linkLeads()">
          <v-icon small class="me-1">mdi-account-multiple-plus-outline</v-icon>{{ $t('crmLinkExisting') }}
        </v-btn>
        <v-btn v-if="canExport" outlined small color="grey darken-1" class="lp__btn" :loading="exporting" @click="exportXlsx">
          <v-icon small class="me-1">mdi-microsoft-excel</v-icon>{{ $t('crmExport') }}
        </v-btn>
        <v-btn v-if="canCreate" depressed small color="primary" class="lp__btn" @click="shell.newLead()">
          <v-icon small class="me-1">mdi-plus</v-icon>{{ $t('crmNewLead') }}
          <kbd v-if="!isMobile" class="lp__kbd">N</kbd>
        </v-btn>
      </template>
      <template #summary>{{ $t('crmTotalRecords', { n: num(total) }) }}</template>
    </crm-toolbar>

    <div class="lp__stats">
      <button
        v-for="s in statStrip"
        :key="s.key"
        type="button"
        class="lp__stat"
        :class="{ on: filters.status.length === 1 && filters.status[0] === s.key }"
        :style="{ '--c': s.color }"
        @click="toggleStatus(s.key)"
      >
        <span class="lp__stat-n">{{ num(s.count) }}</span>
        <span class="lp__stat-l">{{ s.label }}</span>
      </button>
    </div>

    <div class="lp__body">
      <!-- Board by status -->
      <crm-board
        v-if="view === 'board'"
        type="lead"
        group="crm-leads"
        :columns="columns"
        @drop="onDrop"
        @move="(item, target, from) => moveTo(item, from, target)"
        @load-more="loadColumn($event, true)"
        @add="col => shell.newLead({ status: col.key })"
        @open="open"
        @log="(it, kind, mode) => shell.logActivity('lead', it, kind, mode)"
        @convert="convert"
      />

      <!-- Table -->
      <crm-table
        v-else-if="view === 'table'"
        storage-key="leads"
        :headers="headers"
        :items="rows"
        :total="total"
        :loading="loading"
        :options="tableOptions"
        @update:options="onOptions"
        @open="open"
      >
        <template #bulk="{ ids, clear }">
          <bulk-actions entity="lead" :ids="ids" @done="clear(); reload()" />
        </template>
        <template #[`cell.name`]="{ item }">
          <div class="lp__cell-name">
            <crm-avatar :name="displayName(item)" :size="28" />
            <div>
              <b>{{ displayName(item) }} <span v-if="item.is_customer" class="lp__cust">{{ $t('crmIsCustomer') }}</span></b>
              <small v-if="fullName(item) && leadCompany(item)">{{ leadCompany(item) }}</small>
            </div>
          </div>
        </template>
        <template #[`cell.mobile`]="{ item }"><span class="lp__ltr">{{ leadPhone(item) || (item.user && item.user.email) || item.email || '—' }}</span></template>
        <template #[`cell.status`]="{ item }"><crm-chip :value="item.status" small /></template>
        <template #[`cell.priority`]="{ item }"><crm-chip kind="priority" :value="item.priority" small /></template>
        <template #[`cell.source`]="{ item }">{{ (item.source && item.source.title) || sourceTitle(item.source_id) || '—' }}</template>
        <template #[`cell.owner`]="{ item }">
          <span class="lp__owner"><crm-avatar :user-id="item.owner_user_id" :size="22" />{{ memberName(item.owner_user_id) || $t('crmUnassigned') }}</span>
        </template>
        <template #[`cell.score`]="{ item }">
          <span class="lp__score"><i :style="{ width: (item.score || 0) + '%' }" /></span><small>{{ item.score || 0 }}</small>
        </template>
        <template #[`cell.next`]="{ item }">
          <span :class="'lp__f--' + followState(item.next_follow_up_at)">{{ fmtDate(item.next_follow_up_at, true) || '—' }}</span>
        </template>
        <template #[`cell.created`]="{ item }">{{ fmtDate(item.created_at) }}</template>
        <template #empty><empty-state entity="lead" @create="shell.newLead()" /></template>
      </crm-table>

      <!-- Cards grid / agenda -->
      <div v-else class="lp__scroll" @scroll="onGridScroll">
        <template v-if="view === 'cards'">
          <div class="lp__grid">
            <record-card
              v-for="it in rows"
              :key="it.id"
              type="lead"
              :item="it"
              wide
              show-status
              @open="open"
              @log="(r, kind, mode) => shell.logActivity('lead', r, kind, mode)"
              @convert="convert"
            />
          </div>
        </template>
        <template v-else>
          <section v-for="g in agenda" v-show="g.items.length" :key="g.key" class="lp__group">
            <header class="lp__group-head" :style="{ '--c': g.color }">
              <v-icon small :color="g.color">{{ g.icon }}</v-icon>
              <b>{{ g.label }}</b>
              <span>{{ num(g.items.length) }}</span>
            </header>
            <div
              v-for="it in g.items"
              :key="it.id"
              class="lp__row"
              tabindex="0"
              @click="open(it)"
              @keydown.enter="open(it)"
            >
              <crm-avatar :name="displayName(it)" :size="32" />
              <div class="lp__row-main">
                <b>{{ displayName(it) }}</b>
                <small>{{ [fullName(it) ? leadCompany(it) : '', it.need].filter(Boolean).join(' · ') }}</small>
              </div>
              <crm-chip :value="it.status" small class="lp__hide-sm" />
              <span class="lp__row-date" :class="'lp__f--' + followState(it.next_follow_up_at)">{{ fmtDate(it.next_follow_up_at, true) }}</span>
              <crm-avatar :user-id="it.owner_user_id" :size="24" class="lp__hide-sm" />
              <div class="lp__row-acts" @click.stop>
                <a v-if="leadPhone(it)" :href="'tel:' + leadPhone(it)" class="lp__ico lp__ico--call" @click="shell.logActivity('lead', it, 'call')"><v-icon x-small>mdi-phone</v-icon></a>
                <button type="button" class="lp__ico" :title="$t('crmLogActivity')" @click="shell.logActivity('lead', it)"><v-icon x-small>mdi-plus</v-icon></button>
              </div>
            </div>
          </section>
        </template>
        <empty-state v-if="!loading && !rows.length" entity="lead" @create="shell.newLead()" />
        <div v-if="loading" class="lp__loading"><v-progress-circular indeterminate color="primary" :size="22" :width="2" /></div>
      </div>
    </div>
  </div>
</template>

<script>
import api, { crmError } from '../crmApi'
import {
  crm, can, toParams, pick, fullName, leadCompany, leadPhone, memberName, sourceTitle, fmtDate, followState, download,
  leadStatuses, statusMeta, learnOwners
} from '../crmStore'
import CrmToolbar, { emptyFilters } from '../components/CrmToolbar'
import CrmBoard from '../components/CrmBoard'
import CrmTable from '../components/CrmTable'
import RecordCard from '../components/RecordCard'
import BulkActions from '../components/BulkActions'
import EmptyState from '../components/EmptyState'
import { CrmChip, CrmAvatar } from '../components/CrmBits'

const VIEW_KEY = 'crm-leads-view'
const FILTER_KEY = 'crm-leads-filters'

function readLS (k, fb) {
  try { const v = JSON.parse(localStorage.getItem(k)); return v === null ? fb : v } catch (e) { return fb }
}

export default {
  name: 'CrmLeadsPage',
  components: { CrmToolbar, CrmBoard, CrmTable, RecordCard, BulkActions, EmptyState, CrmChip, CrmAvatar },
  inject: ['shell'],
  data () {
    return {
      view: readLS(VIEW_KEY, 'board'),
      filters: { ...emptyFilters(), ...readLS(FILTER_KEY, {}) },
      columns: [],
      rows: [],
      total: 0,
      page: 1,
      lastPage: 1,
      loading: false,
      exporting: false,
      tableOptions: { page: 1, itemsPerPage: 20, sortBy: [], sortDesc: [] },
      statCounts: {}
    }
  },
  computed: {
    allStatuses () { return leadStatuses() },
    statuses () { return this.allStatuses.map(s => s.key) },
    // board / counters: everything except the archived category
    boardStatuses () { return this.allStatuses.filter(s => s.category !== 'archived') },
    canManage () { return can('pipelines.manage') },
    statusesKey () { return crm.statuses.lead.map(x => x.key + x.title + x.color).join('|') },
    canCreate () { return can('leads.create') },
    canExport () { return can('export.manage') },
    views () {
      return [
        { key: 'board', icon: 'mdi-view-column-outline', label: this.$t('crmViewBoard') },
        { key: 'table', icon: 'mdi-table', label: this.$t('crmViewTable') },
        { key: 'cards', icon: 'mdi-view-grid-outline', label: this.$t('crmViewCards') },
        { key: 'agenda', icon: 'mdi-format-list-text', label: this.$t('crmViewAgenda') }
      ]
    },
    headers () {
      return [
        { text: this.$t('crmName'), value: 'name', fixed: true, sortable: false },
        { text: this.$t('crmContact'), value: 'mobile', sortable: false },
        { text: this.$t('crmStatus'), value: 'status' },
        { text: this.$t('crmPriority'), value: 'priority' },
        { text: this.$t('crmSource'), value: 'source', sortable: false },
        { text: this.$t('crmOwner'), value: 'owner', sortable: false },
        { text: this.$t('crmScore'), value: 'score' },
        { text: this.$t('crmNextFollowUp'), value: 'next' },
        { text: this.$t('crmCreatedAt'), value: 'created' }
      ]
    },
    statStrip () {
      return this.boardStatuses.map(s => ({ key: s.key, label: this.label(s), color: s.color || statusMeta(s.key).color, count: this.statCounts[s.key] || 0 }))
    },
    agenda () {
      const groups = {
        overdue: { key: 'overdue', label: this.$t('crmGroupOverdue'), icon: 'mdi-alarm', color: '#c0392b', items: [] },
        today: { key: 'today', label: this.$t('crmGroupToday'), icon: 'mdi-calendar-today', color: '#2166d1', items: [] },
        future: { key: 'future', label: this.$t('crmGroupUpcoming'), icon: 'mdi-calendar-arrow-left', color: '#0f9d76', items: [] },
        none: { key: 'none', label: this.$t('crmGroupNoFollow'), icon: 'mdi-calendar-remove-outline', color: '#d9822b', items: [] }
      }
      this.rows.forEach(r => groups[followState(r.next_follow_up_at)].items.push(r))
      groups.future.items.sort((a, b) => String(a.next_follow_up_at).localeCompare(String(b.next_follow_up_at)))
      return Object.values(groups)
    }
  },
  watch: {
    statusesKey () { this.reload() },
    view (v) {
      try { localStorage.setItem(VIEW_KEY, JSON.stringify(v)) } catch (e) { /* ignore */ }
      this.reload()
    }
  },
  created () {
    this.shell.$on('changed', this.onChanged)
    this.reload()
  },
  beforeDestroy () { this.shell.$off('changed', this.onChanged) },
  methods: {
    fullName,
    memberName,
    sourceTitle,
    fmtDate,
    followState,
    num (v) { return Number(v || 0).toLocaleString(this.$i18n.locale === 'en' ? 'en-US' : 'fa-IR') },
    label (s) { return s.title || this.$t('crmStatus_' + s.key) },
    leadCompany,
    leadPhone,
    displayName (r) { return fullName(r) || leadCompany(r) || '#' + r.id },
    params () { return toParams(this.filters, 'lead') },
    setFilters (f) {
      this.filters = f
      const { search, ...keep } = f
      try { localStorage.setItem(FILTER_KEY, JSON.stringify(keep)) } catch (e) { /* ignore */ }
      this.reload()
    },
    toggleStatus (s) {
      const on = this.filters.status.length === 1 && this.filters.status[0] === s
      this.setFilters({ ...this.filters, status: on ? [] : [s] })
    },
    reload () {
      this.loadStats()
      if (this.view === 'board') return this.loadBoard()
      if (this.view === 'table') return this.loadTable()
      this.page = 1
      this.rows = []
      return this.loadGrid()
    },
    async loadStats () {
      const base = { ...this.params() }
      delete base.status
      const res = await Promise.all(this.boardStatuses.map(({ key: s }) =>
        api.leads({ ...base, status: [s], itemsPerPage: 1 }).then(r => [s, r.total || 0]).catch(() => [s, 0])))
      this.statCounts = Object.fromEntries(res)
    },
    async loadBoard () {
      const only = this.filters.status.length ? this.filters.status : null
      this.columns = this.boardStatuses.filter(s => !only || only.includes(s.key)).map(s => ({
        key: s.key,
        title: this.label(s),
        color: s.color || statusMeta(s.key).color,
        kind: s.category === 'won' ? 'won' : (s.category === 'lost' ? 'lost' : 'open'),
        category: s.category,
        items: [],
        count: 0,
        page: 0,
        lastPage: 1,
        hasMore: false,
        loading: true
      }))
      await Promise.all(this.columns.map(c => this.loadColumn(c)))
      this.total = this.columns.reduce((a, c) => a + (c.count || 0), 0)
    },
    async loadColumn (col, more = false) {
      if (more && (!col.hasMore || col.loading)) return
      col.loading = true
      try {
        const page = more ? col.page + 1 : 1
        const res = await api.leads({ ...this.params(), status: [col.key], page, itemsPerPage: 30 })
        const items = pick(res)
        learnOwners(items)
        col.items = more ? col.items.concat(items) : items
        col.page = res.current_page || page
        col.lastPage = res.last_page || 1
        col.count = res.total !== undefined ? res.total : col.items.length
        col.hasMore = col.page < col.lastPage
      } catch (e) {
        this.shell.toast(crmError(e).message, { error: true })
      } finally {
        col.loading = false
      }
    },
    onOptions (o) {
      this.tableOptions = o
      this.loadTable()
    },
    async loadTable () {
      this.loading = true
      try {
        const o = this.tableOptions
        const sortMap = { created: 'created_at', next: 'next_follow_up_at' }
        const res = await api.leads({
          ...this.params(),
          page: o.page,
          itemsPerPage: o.itemsPerPage,
          options: { page: o.page, itemsPerPage: o.itemsPerPage, sortDesc: o.sortDesc },
          sortBy: (o.sortBy || []).map(s => sortMap[s] || s)
        })
        this.rows = pick(res)
        learnOwners(this.rows)
        this.total = res.total || this.rows.length
      } catch (e) {
        this.shell.toast(crmError(e).message, { error: true })
      } finally {
        this.loading = false
      }
    },
    async loadGrid () {
      this.loading = true
      try {
        const res = await api.leads({ ...this.params(), page: this.page, itemsPerPage: 40 })
        const items = pick(res)
        learnOwners(items)
        this.rows = this.page === 1 ? items : this.rows.concat(items)
        this.total = res.total || this.rows.length
        this.lastPage = res.last_page || 1
      } catch (e) {
        this.shell.toast(crmError(e).message, { error: true })
      } finally {
        this.loading = false
      }
    },
    onGridScroll (e) {
      const el = e.target
      if (!this.loading && this.page < this.lastPage && el.scrollTop + el.clientHeight > el.scrollHeight - 200) {
        this.page++
        this.loadGrid()
      }
    },
    open (r, tab) {
      this.shell.openRecord('lead', r, tab)
    },
    convert (r) { this.shell.convertLead(r) },
    onDrop ({ item, from, to }) {
      this.applyMove(item, from, to, true)
    },
    moveTo (item, from, target) {
      // manual "move to" from the card menu: relocate first, then persist
      from.items = from.items.filter(i => i.id !== item.id)
      target.items.unshift(item)
      this.applyMove(item, from, target, true)
    },
    revert (item, from, to) {
      to.items = to.items.filter(i => i.id !== item.id)
      from.items.unshift(item)
    },
    async applyMove (item, from, to) {
      const prev = item.status
      // categories drive the behaviour (custom statuses included): won → convert, lost → reject with reason
      if (to.key === 'converted' || (to.category === 'won' && !item.deal_id)) {
        this.revert(item, from, to)
        this.shell.convertLead(item)
        return
      }
      if (to.category === 'lost' || to.category === 'archived') {
        const reason = await this.shell.ask(this.$t('crmRejectReason'), this.$t('crmReason'))
        if (!reason) { this.revert(item, from, to); return }
        try {
          await api.rejectLead(item.id, { status: to.key, reason })
          this.afterMove(item, from, to, prev)
        } catch (e) {
          this.revert(item, from, to)
          this.shell.toast(crmError(e).message, { error: true })
        }
        return
      }
      try {
        item.status = to.key
        await api.updateLead(item.id, { status: to.key })
        this.afterMove(item, from, to, prev)
      } catch (e) {
        item.status = prev
        this.revert(item, from, to)
        this.shell.toast(crmError(e).message, { error: true })
      }
    },
    afterMove (item, from, to, prev) {
      item.status = to.key
      from.count = Math.max(0, (from.count || 1) - 1)
      to.count = (to.count || 0) + 1
      this.statCounts = { ...this.statCounts, [from.key]: from.count, [to.key]: to.count }
      this.shell.toast(this.$t('crmMovedTo', { to: to.title }), {
        undo: async () => {
          if (to.category === 'lost' || to.category === 'archived') await api.restoreLead(item.id)
          await api.updateLead(item.id, { status: prev })
          this.reload()
        }
      })
    },
    onChanged ({ type }) {
      if (type === 'lead') this.reload()
    },
    async exportXlsx () {
      this.exporting = true
      try {
        const blob = await api.exportList({ ...this.params(), entity_type: 'lead', format: 'xlsx' })
        download(blob, 'crm-leads.xlsx')
      } catch (e) {
        this.shell.toast(crmError(e).message || this.$t('crmExportFailed'), { error: true })
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.lp { display: flex; flex-direction: column; gap: 12px; height: 100%; min-height: 0; }
.lp__cust { font-size: .66rem; font-weight: 700; padding: 1px 6px; border-radius: 6px; background: rgba(15, 157, 118, .12); color: #0f9d76; margin-inline-start: 4px; }
.lp__icon { border-radius: 6px !important; }
.lp__btn { border-radius: 6px !important; letter-spacing: 0; height: 36px !important; }
.lp__kbd { margin-inline-start: 8px; font-size: .65rem; padding: 0 5px; border-radius: 4px; background: rgba(255, 255, 255, .25); box-shadow: none; }
.lp__stats { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 8px; flex-shrink: 0; }
.lp__stat { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; padding: 10px 14px; border-radius: 6px; background: #fff; border: 1px solid rgba(15, 23, 42, .08); border-top: 3px solid var(--c); text-align: start; transition: all .15s; }
.theme--dark .lp__stat { background: #1e1e1e; border-color: rgba(255, 255, 255, .08); border-top-color: var(--c); }
.lp__stat:hover { box-shadow: 0 2px 8px rgba(15, 23, 42, .07); }
.lp__stat.on { background: rgba(25, 118, 210, .06); border-color: var(--c); }
.lp__stat-n { font-weight: 800; font-size: 1.15rem; }
.lp__stat-l { font-size: .74rem; opacity: .65; }
.lp__body { flex: 1; min-height: 0; }
.lp__scroll { height: 100%; overflow-y: auto; overflow-x: hidden; padding-bottom: 20px; }
.lp__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 10px; }
.lp__group { margin-bottom: 18px; }
.lp__group-head { display: flex; align-items: center; gap: 8px; padding: 6px 4px 8px; font-size: .85rem; position: sticky; top: 0; z-index: 1; background: #f3f5f9; }
.theme--dark .lp__group-head { background: #121212; }
.lp__group-head span { font-size: .7rem; padding: 0 7px; border-radius: 6px; background: rgba(127, 127, 127, .14); }
.lp__row { display: flex; align-items: center; gap: 12px; padding: 10px 14px; background: #fff; border: 1px solid rgba(15, 23, 42, .07); border-radius: 6px; margin-bottom: 6px; cursor: pointer; transition: box-shadow .15s; outline: none; }
.lp__row:hover, .lp__row:focus-visible { box-shadow: 0 3px 10px rgba(15, 23, 42, .08); }
.theme--dark .lp__row { background: #1e1e1e; border-color: rgba(255, 255, 255, .08); }
.lp__row-main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.lp__row-main b { font-size: .86rem; }
.lp__row-main small { font-size: .74rem; opacity: .6; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lp__row-date { font-size: .76rem; min-width: 110px; text-align: end; opacity: .75; }
.lp__row-acts { display: flex; gap: 4px; }
.lp__ico { width: 28px; height: 28px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; background: rgba(127, 127, 127, .1); text-decoration: none; }
.lp__ico--call { background: rgba(15, 157, 118, .12); }
.lp__ico--call ::v-deep .v-icon { color: #0f9d76 !important; }
.lp__f--overdue { color: #c0392b; font-weight: 600; opacity: 1; }
.lp__f--today { color: var(--v-primary-base); font-weight: 600; opacity: 1; }
.lp__cell-name { display: flex; align-items: center; gap: 10px; }
.lp__cell-name > div { display: flex; flex-direction: column; line-height: 1.3; }
.lp__cell-name small { opacity: .6; font-size: .72rem; }
.lp__owner { display: inline-flex; align-items: center; gap: 6px; }
.lp__score { display: inline-block; width: 48px; height: 4px; border-radius: 4px; background: rgba(127, 127, 127, .15); overflow: hidden; margin-inline-end: 6px; vertical-align: middle; }
.lp__score i { display: block; height: 100%; background: #0f9d76; }
.lp__ltr { direction: ltr; display: inline-block; }
.lp__loading { display: flex; justify-content: center; padding: 16px; }
@media (max-width: 799px) {
  .lp__stats { display: flex; overflow-x: auto; }
  .lp__stat { flex: 0 0 110px; }
  .lp__hide-sm { display: none !important; }
  .lp__row-date { min-width: 0; }
}
</style>
